/**
 * One error per registered resource slot whose reference names an id the file never declares. The loader resolves it
 * while it tokenises the value, before any setter, and fails the whole load on a miss (`resource_format_text.cpp:113`,
 * and the ext twin at `:138`). The registry says which keys are slots (`createResourceReferenceValidator` marks them).
 */
import { FILE_DIAGNOSTICS } from './fileDiagnostics.js';
import { armDiagnostic } from './ruleArms.js';
import { propertyLocation } from './sourceLocation.js';
import { validatorRegistry } from './ValidatorRegistry.js';
import { isResourceSlotValidator } from './validators/resourceValidators.js';
import { resourceRef } from '../godot/variant/resourceRef.js';
/**
 * Whether the declaration for `key` is a resource slot. An indexed family registers one dispatcher for
 * `item_<i>/<leaf>`, which forwards to exactly one leaf. So its accepting a reference literal means a resource leaf did:
 * no family that declares a resource leaf declares a `v.any()` leaf beside it.
 */
function isResourceSlot(declaration, key, value) {
    if (isResourceSlotValidator(declaration))
        return true;
    const leaves = declaration.leaves;
    if (!leaves?.some(isResourceSlotValidator))
        return false;
    return declaration(key, value, 0) === null;
}
function sweep(owner, declared, declaredLater, lines, into) {
    for (const [key, value] of Object.entries(owner.rawProperties)) {
        const ref = resourceRef(value.trim());
        // Not a well-formed reference: its format is the strict parser's diagnostic, and a second error naming a
        // missing resource would be wrong.
        if (ref === null)
            continue;
        const table = ref.kind === 'SubResource' ? declared.int : declared.ext;
        if (table.has(ref.id))
            continue;
        const declaration = validatorRegistry.declarationFor(owner.type, key);
        if (!declaration || !isResourceSlot(declaration, key, value))
            continue;
        const where = ref.kind === 'SubResource' && declaredLater.has(ref.id)
            ? 'an id declared only later in this file, which the loader has not created yet'
            : 'an id this file never declares';
        into.push(armDiagnostic(FILE_DIAGNOSTICS.danglingResourceReference, owner, `Property '${key}' references ${value.trim()}, ${where}. Godot fails to load the file.`, propertyLocation(lines, owner.built, key)));
    }
}
/** Each dangling reference, on the line of the property that holds it. */
export function danglingResourceDiagnostics(scene, lines) {
    const diagnostics = [];
    const ext = new Set(scene.externalResources.map((r) => r.id));
    const int = new Set();
    const all = new Set(scene.internalResources.map((r) => r.id));
    // File order: `int_resources[id] = res` lands as each heading is read (`:629`), ahead of its body, so a body sees
    // the ids above it and its own, and a forward reference dangles.
    for (const resource of scene.internalResources) {
        int.add(resource.id);
        const owner = {
            built: resource,
            name: resource.id,
            type: resource.type,
            rawProperties: resource.data,
        };
        sweep(owner, { ext, int }, all, lines, diagnostics);
    }
    const declared = { ext, int };
    const none = new Set();
    // A `.tres` body parses at `:774` with the ext and sub callbacks set at `:1191-1192`, after every sub-resource.
    if (scene.mainResource) {
        const { mainResource } = scene;
        const owner = {
            built: mainResource,
            name: '<unknown>',
            type: scene.resourceType ?? '',
            rawProperties: mainResource.data,
        };
        sweep(owner, declared, none, lines, diagnostics);
    }
    const visit = (node) => {
        const { name, type, rawProperties } = node;
        sweep({ built: node, name, type, rawProperties }, declared, none, lines, diagnostics);
        for (const child of node.children)
            visit(child);
    };
    for (const node of scene.nodes)
        visit(node);
    return diagnostics;
}
//# sourceMappingURL=danglingResources.js.map